"use client";

import { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, Transition } from "@headlessui/react";
import {
    X,
    Link as LinkIcon,
    FileText,
    AlignLeft,
    Eye,
    Download,
    ExternalLink,
    File,
    FileImage,
    Presentation,
    Sheet,
    Loader2,
    BookOpen,
} from "lucide-react";
import ResourceViewerModal from "@/components/admin/ResourceViewerModal";
import type { Resource } from "@/components/admin/ResourceList";
import { courseResourcesQueryKey, fetchCourseResources } from "@/lib/student/course-resources-query";

interface ResourceDrawerProps {
    courseId: string;
    isOpen: boolean;
    onClose: () => void;
    initialModuleId?: number | null;
    initialLessonId?: number | null;
    moduleName?: string | null;
    lessonName?: string | null;
    modules?: Array<{
        id: number;
        title: string;
        level?: { id: number; name: string } | null;
        lessons?: Array<{ canonicalLessonId?: number | null; id: string | number; title: string }>;
    }>;
}

type FilterScope = "all" | "course" | "module" | "lesson";

function getFileIcon(mimeType: string | null) {
    if (!mimeType) return <File className="h-4 w-4 text-slate-400" />;
    if (mimeType.startsWith("image/")) return <FileImage className="h-4 w-4 text-purple-500" />;
    if (mimeType === "application/pdf") return <FileText className="h-4 w-4 text-red-500" />;
    if (mimeType.includes("powerpoint") || mimeType.includes("presentation"))
        return <Presentation className="h-4 w-4 text-orange-500" />;
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet"))
        return <Sheet className="h-4 w-4 text-emerald-600" />;
    return <FileText className="h-4 w-4 text-blue-500" />;
}

function getTypeIcon(resource: Resource) {
    if (resource.type === "LINK") return <LinkIcon className="h-4 w-4 text-indigo-500" />;
    if (resource.type === "FILE") return getFileIcon(resource.mime_type);
    return <AlignLeft className="h-4 w-4 text-emerald-500" />;
}

function ResourceCard({
    resource,
    onView,
}: {
    resource: Resource;
    onView: () => void;
}) {
    return (
        <div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2.5 hover:border-gray-200 hover:shadow-sm transition-all">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50">
                {getTypeIcon(resource)}
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{resource.title}</p>
                <p className="mt-0.5 text-[11px] text-gray-400 truncate">
                    {resource.type === "FILE" && resource.file_name}
                    {resource.type === "LINK" && resource.content}
                    {resource.type === "RICH_TEXT" && "Rich text"}
                </p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                    type="button"
                    onClick={onView}
                    className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                >
                    <Eye className="h-3.5 w-3.5" />
                </button>
                {resource.type === "FILE" && (
                    <a
                        href={resource.content}
                        download={resource.file_name ?? true}
                        className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                    >
                        <Download className="h-3.5 w-3.5" />
                    </a>
                )}
                {resource.type === "LINK" && (
                    <a
                        href={resource.content}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                    >
                        <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                )}
            </div>
        </div>
    );
}

function SectionHeader({ label, centered = false }: { label: string; centered?: boolean }) {
    if (centered) {
        return (
            <div className="flex items-center gap-3 py-2">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 whitespace-nowrap">
                    {label}
                </span>
                <div className="flex-1 border-t border-gray-200" />
            </div>
        );
    }
    return (
        <div className="flex items-center gap-2 pt-1 pb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</span>
            <div className="flex-1 border-t border-gray-100" />
        </div>
    );
}

function GroupedResourceList({
    resources,
    onView,
    modules = [],
}: {
    resources: Resource[];
    onView: (r: Resource) => void;
    modules?: ResourceDrawerProps["modules"];
}) {
    const modulesList = modules ?? [];
    // Module/lesson titles come from the course tree the drawer was opened
    // from, not `resource.modules`/`resource.lessons` — the FastAPI response
    // never populates those legacy Next.js relation shapes.
    const lessonTitleById = new Map<number, string>();
    for (const mod of modulesList) {
        for (const lesson of mod.lessons ?? []) {
            if (typeof lesson.canonicalLessonId === "number") {
                lessonTitleById.set(lesson.canonicalLessonId, lesson.title);
            }
        }
    }

    const courseResources = resources.filter((r) => !r.module_id && !r.lesson_id);

    // Group module resources by module
    const moduleGroups = new Map<number, { name: string; levelName?: string; resources: Resource[] }>();
    for (const r of resources) {
        if (r.module_id && !r.lesson_id) {
            const mod = modulesList.find((m) => m.id === r.module_id);
            const existing = moduleGroups.get(r.module_id);
            if (existing) {
                existing.resources.push(r);
            } else {
                moduleGroups.set(r.module_id, {
                    name: mod?.title ?? `Module ${r.module_id}`,
                    levelName: mod?.level?.name,
                    resources: [r],
                });
            }
        }
    }

    // Group lesson resources by lesson
    const lessonGroups = new Map<number, { name: string; resources: Resource[] }>();
    for (const r of resources) {
        if (r.lesson_id) {
            const existing = lessonGroups.get(r.lesson_id);
            if (existing) {
                existing.resources.push(r);
            } else {
                lessonGroups.set(r.lesson_id, {
                    name: lessonTitleById.get(r.lesson_id) ?? `Lesson ${r.lesson_id}`,
                    resources: [r],
                });
            }
        }
    }

    // Group modules by level
    const byLevel = new Map<string, Array<{ moduleId: number; group: { name: string; levelName?: string; resources: Resource[] } }>>();
    for (const [moduleId, group] of moduleGroups) {
        const levelKey = group.levelName ?? "no-level";
        const arr = byLevel.get(levelKey) ?? [];
        arr.push({ moduleId, group });
        byLevel.set(levelKey, arr);
    }

    return (
        <div className="space-y-4">
            {courseResources.length > 0 && (
                <div>
                    <SectionHeader label="Course" centered />
                    <div className="space-y-2">
                        {courseResources.map((r) => (
                            <ResourceCard key={r.id} resource={r} onView={() => onView(r)} />
                        ))}
                    </div>
                </div>
            )}

            {[...byLevel.entries()].map(([levelKey, levelModules]) => (
                <div key={`level-${levelKey}`}>
                    {levelKey !== "no-level" && <SectionHeader label={levelKey} centered />}
                    <div className="space-y-3">
                        {levelModules.map(({ moduleId, group }) => (
                            <div key={`mod-${moduleId}`}>
                                <SectionHeader label={group.name} />
                                <div className="space-y-2">
                                    {group.resources.map((r) => (
                                        <ResourceCard key={r.id} resource={r} onView={() => onView(r)} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {[...lessonGroups.entries()].map(([lessonId, group]) => (
                <div key={`les-${lessonId}`}>
                    <SectionHeader label={group.name} />
                    <div className="space-y-2">
                        {group.resources.map((r) => (
                            <ResourceCard key={r.id} resource={r} onView={() => onView(r)} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function ResourceDrawer({
    courseId,
    isOpen,
    onClose,
    initialModuleId = null,
    initialLessonId = null,
    moduleName = null,
    lessonName = null,
    modules = [],
}: ResourceDrawerProps) {
    const [filter, setFilter] = useState<FilterScope>("all");
    const [viewingResource, setViewingResource] = useState<Resource | null>(null);

    // Determine default filter based on initial props
    useEffect(() => {
        if (!isOpen) return;
        if (initialLessonId) {
            setFilter("lesson");
        } else if (initialModuleId) {
            setFilter("module");
        } else {
            setFilter("all");
        }
    }, [isOpen, initialModuleId, initialLessonId]);

    // Reads the same whole-course cache entry CourseDetail/LessonPlayer
    // populate — see F7 in docs/api_performance_audit.md — instead of
    // issuing its own fetch every time the drawer opens.
    const resourcesQuery = useQuery({
        queryKey: courseResourcesQueryKey(courseId),
        queryFn: () => fetchCourseResources(courseId),
        staleTime: 60_000,
        enabled: isOpen,
    });
    const loading = resourcesQuery.isLoading;
    const resources = useMemo(
        () => (resourcesQuery.data ?? []) as Resource[],
        [resourcesQuery.data],
    );

    // Filter by scope (no lock filtering - show all resources)
    const filtered = resources
        .filter((r) => {
            if (filter === "all") return true;
            if (filter === "course") return !r.module_id && !r.lesson_id;
            if (filter === "module") return r.module_id === initialModuleId;
            if (filter === "lesson") return r.lesson_id === initialLessonId;
            return true;
        })
        .sort((a, b) => {
            // Course-level first, then module-level, then lesson-level
            const scopeWeight = (r: Resource) => {
                if (!r.module_id && !r.lesson_id) return 0;
                if (r.module_id && !r.lesson_id) return 1;
                return 2;
            };
            return scopeWeight(a) - scopeWeight(b);
        });

    const hasContext = !!initialModuleId || !!initialLessonId;
    const filterPills: { key: FilterScope; label: string; show: boolean }[] = [
        { key: "all", label: "All", show: hasContext },
        { key: "module", label: moduleName ?? "Module", show: !!initialModuleId },
        { key: "lesson", label: lessonName ?? "Lesson", show: !!initialLessonId },
    ];

    return (
        <>
            <Transition.Root show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={onClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-slate-900/40 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-hidden">
                        <div className="absolute inset-0 overflow-hidden">
                            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                                <Transition.Child
                                    as={Fragment}
                                    enter="transform transition ease-out duration-300"
                                    enterFrom="translate-x-full"
                                    enterTo="translate-x-0"
                                    leave="transform transition ease-in duration-200"
                                    leaveFrom="translate-x-0"
                                    leaveTo="translate-x-full"
                                >
                                    <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                                        <div className="flex h-full flex-col bg-white shadow-xl">
                                            {/* Header */}
                                            <div className="border-b border-gray-100 px-5 py-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <BookOpen className="h-5 w-5 text-indigo-600" />
                                                        <Dialog.Title className="text-base font-bold text-gray-900">
                                                            Resources
                                                        </Dialog.Title>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={onClose}
                                                        className="rounded-md p-1 text-gray-400 hover:text-gray-600"
                                                    >
                                                        <X className="h-5 w-5" />
                                                    </button>
                                                </div>

                                                {/* Filter pills — only shown when there's module/lesson context */}
                                                {hasContext && (
                                                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                                                        {filterPills
                                                            .filter((p) => p.show)
                                                            .map((pill) => (
                                                                <button
                                                                    key={pill.key}
                                                                    type="button"
                                                                    onClick={() => setFilter(pill.key)}
                                                                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                                                        filter === pill.key
                                                                            ? "bg-indigo-600 text-white"
                                                                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                                                    }`}
                                                                >
                                                                    {pill.label}
                                                                </button>
                                                            ))}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Resource list */}
                                            <div className="flex-1 overflow-y-auto px-5 py-4">
                                                {loading ? (
                                                    <div className="flex items-center justify-center py-12">
                                                        <Loader2 className="h-6 w-6 text-indigo-600 animate-spin" />
                                                    </div>
                                                ) : filtered.length === 0 ? (
                                                    <div className="text-center py-12">
                                                        <BookOpen className="mx-auto h-8 w-8 text-gray-300" />
                                                        <p className="mt-2 text-sm text-gray-400">
                                                            No resources found
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <GroupedResourceList
                                                        resources={filtered}
                                                        onView={setViewingResource}
                                                        modules={modules}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            <ResourceViewerModal
                resource={viewingResource}
                isOpen={viewingResource !== null}
                onClose={() => setViewingResource(null)}
            />
        </>
    );
}
